import { AddPaymentMethodDialog } from "@/components/billing/AddPaymentMethodDialog.tsx";
import {
  isLastMethodBlocked,
  useCancelPlan,
  useChangePlan,
  useCompleteCheckout,
  useDeletePaymentMethod,
  useKeepCurrentPlan,
  usePaymentMethods,
  usePlan,
  useRefreshPaymentMethods,
  useResumePlan,
  useSetDefaultPaymentMethod,
  useStartCheckout,
} from "@/components/billing/useBilling.tsx";
import { PlanChangeDialog, type PlanChangeIntent } from "@/components/billing/PlanChangeDialog.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import {
  catalogEntry,
  FREE_PRICE_LABEL,
  formatDate,
  formatPricePerInterval,
  formatResetMoment,
  intervalAdjective,
  intervalAdverb,
  multipleLabel,
  TIER_TAGLINES,
} from "@/components/billing/planCopy.ts";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { ButtonGroup } from "@/components/ui/button-group.tsx";
import {
  planChangeTiming,
  type CatalogEntryResp,
  type PlanChangeTiming,
  type PlanResp,
  type ScheduledChangeResp,
} from "@/types/billing.ts";
import {
  BILLING_INTERVALS,
  PAID_TIERS,
  PLAN_TIER_LABELS as TIER_LABELS,
  paidTiersAbove,
  type BillingInterval,
  type PaidTier,
  type PlanTier,
} from "@/types/entitlements.ts";
import type { PaymentMethodResp } from "@/types/wallet.ts";
import { CirclePlus, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useSearch } from "wouter";

const BILLING_PATH = "/settings/billing";

/** A card is expired once its expiry month has fully passed. */
function isExpired(method: PaymentMethodResp, now = new Date()): boolean {
  const endOfExpiryMonth = new Date(Date.UTC(method.expYear, method.expMonth, 1));
  return endOfExpiryMonth <= now;
}

function formatBrand(brand: string): string {
  if (brand === "amex") return "Amex";
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

export const BillingPanel = () => {
  useCheckoutReturn();
  return (
    <div className="w-full pb-12 space-y-6">
      <div>
        <h2 className="text-base font-semibold mb-2">Plan</h2>
        <PlanCard />
      </div>
      <div>
        <h2 className="text-base font-semibold mb-2">Payment methods</h2>
        <PaymentMethodsCard />
      </div>
    </div>
  );
};

/**
 * Settles the plan when Stripe redirects back to `?checkout=…`.
 *
 * The query parameters are stripped as soon as they are read, which is what
 * stops a reload — or a shared URL — from re-running the settle. The ref guard
 * covers the render between the mutation firing and the URL actually changing.
 */
function useCheckoutReturn(): void {
  const search = useSearch();
  const [, navigate] = useLocation();
  const { mutate: completeCheckout } = useCompleteCheckout();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) {
      return;
    }
    const params = new URLSearchParams(search);
    const outcome = params.get("checkout");
    if (!outcome) {
      return;
    }
    handled.current = true;

    const sessionId = params.get("session_id");
    if (outcome === "success" && sessionId) {
      completeCheckout(sessionId);
    } else if (outcome === "canceled") {
    }
    navigate(BILLING_PATH, { replace: true });
  }, [search, navigate, completeCheckout]);
}

export const PlanCard = () => {
  const { data: plan, isLoading } = usePlan();
  const changePlan = useChangePlan();
  const cancelPlan = useCancelPlan();
  const resumePlan = useResumePlan();
  const keepCurrentPlan = useKeepCurrentPlan();
  const [intent, setIntent] = useState<PlanChangeIntent | null>(null);
  const [chosenInterval, setChosenInterval] = useState<BillingInterval | null>(null);

  if (isLoading) {
    return (
      <div className="py-6 flex justify-center">
        <Loader size="sm" />
      </div>
    );
  }
  if (!plan) {
    return null;
  }

  const subscription = plan.subscription;
  const periodEnd = subscription?.currentPeriodEnd;
  const scheduledChange = subscription?.scheduledChange ?? null;
  // A dunning subscription says two opposite things depending on where it is
  // in its grace period — still working, or already ended — so the deadline
  // rather than the status decides the copy.
  const graceEndsAt = subscription?.status === "past_due" ? subscription.graceEndsAt : null;
  // The subscription's own tier, not the resolved one: once the grace period
  // lapses the account is on Free while the subscription still sells Pro.
  const dunningTier = subscription?.tier ?? plan.tier;
  const graceExpired = graceEndsAt !== null && new Date(graceEndsAt) <= new Date();
  // Opens on the interval the user already pays on, so the options start out
  // describing their own plan.
  const interval = chosenInterval ?? subscription?.interval ?? "month";
  const multiple = multipleLabel(catalogEntry(plan, plan.tier));
  const lockReason = changeLockReason(plan);

  const confirm = (next: PlanChangeIntent) => {
    setIntent(null);
    if (next.kind === "cancel") {
      cancelPlan.mutate();
    } else {
      changePlan.mutate({ tier: next.tier, interval: next.interval });
    }
  };

  const busy =
    changePlan.isPending ||
    cancelPlan.isPending ||
    resumePlan.isPending ||
    keepCurrentPlan.isPending;

  return (
    <div className="rounded-lg bg-white p-4 space-y-4" data-testid="plan-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium flex items-center gap-2">
            <span>{TIER_LABELS[plan.tier]}</span>
            {multiple && <Badge variant="secondary">{multiple}</Badge>}
            {subscription?.status === "past_due" && (
              <Badge variant="destructive">Payment failed</Badge>
            )}
          </div>
          {plan.grant && (
            <div className="text-xs text-muted-foreground" data-testid="plan-grant">
              Complimentary until {formatDate(plan.grant.endsAt)}
            </div>
          )}
          {periodEnd && !graceEndsAt && (
            <div className="text-xs text-muted-foreground">
              {subscription?.interval && `Billed ${intervalAdverb(subscription.interval)} · `}
              {subscription?.cancelAtPeriodEnd ? "Ends" : "Renews"} {formatDate(periodEnd)}
            </div>
          )}
        </div>
      </div>

      {graceEndsAt && (
        <div
          className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid="plan-dunning-banner"
        >
          {graceExpired ? (
            <>
              We couldn't charge your card, so your {TIER_LABELS[dunningTier]} plan has ended. Your
              data, connectors, and routines are all still here — update your card to put everything
              back.
            </>
          ) : (
            <>
              We couldn't charge your card. Your {TIER_LABELS[dunningTier]} plan keeps working as
              normal until {formatDate(graceEndsAt)}, then moves to Free. Update your card to keep
              it.
            </>
          )}
        </div>
      )}

      {subscription?.cancelAtPeriodEnd && periodEnd && (
        <div
          className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground"
          data-testid="plan-cancel-banner"
        >
          Your plan ends {formatDate(periodEnd)}. Everything keeps working until then.
        </div>
      )}

      {scheduledChange && !subscription?.cancelAtPeriodEnd && (
        <div
          className="rounded-md bg-muted px-3 py-2 text-xs flex flex-wrap items-center justify-between gap-2"
          data-testid="plan-scheduled-banner"
        >
          <span className="text-muted-foreground">
            {scheduledChangeSentence(dunningTier, scheduledChange)}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => keepCurrentPlan.mutate()}
            data-statsig-name="billing-plan-keep-current"
          >
            {keepCurrentLabel(dunningTier, scheduledChange)}
          </Button>
        </div>
      )}

      <UsageMeter plan={plan} />

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-medium text-muted-foreground">Plans</span>
          <IntervalToggle value={interval} onChange={setChosenInterval} />
        </div>
        {lockReason && (
          <div className="text-xs text-muted-foreground" data-testid="plan-locked">
            {lockReason}
          </div>
        )}
        <PlanOptions
          plan={plan}
          interval={interval}
          isBusy={busy}
          isLocked={lockReason !== null}
          onChange={setIntent}
        />
      </div>

      {subscription && (
        <div className="flex flex-wrap items-center gap-2">
          {subscription.cancelAtPeriodEnd ? (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => resumePlan.mutate()}
              data-statsig-name="billing-plan-resume"
            >
              Resume plan
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => setIntent({ kind: "cancel" })}
              data-statsig-name="billing-plan-cancel"
            >
              Cancel plan
            </Button>
          )}
        </div>
      )}

      <PlanChangeDialog
        intent={intent}
        plan={plan}
        pending={busy}
        onConfirm={confirm}
        onOpenChange={(open) => !open && setIntent(null)}
      />
    </div>
  );
};

/**
 * How much of this period's allowance is used, as the percent the server
 * computed — never unit counts, which mean nothing to a user. The percent
 * reads 100 only once work is actually refused.
 */
const UsageMeter = ({ plan }: { plan: PlanResp }) => {
  const pct = plan.percentUsed;
  if (pct === null) {
    return <div className="text-xs text-muted-foreground">No usage limit right now</div>;
  }
  return (
    <div className="space-y-1.5" data-testid="usage-meter">
      <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
        <span>
          {pct}% of this {plan.allowancePeriod}'s usage
        </span>
        <span>Resets {formatResetMoment(plan.resetsAt)}</span>
      </div>
      <div
        className="h-1.5 w-full rounded-full bg-muted overflow-hidden"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Usage this ${plan.allowancePeriod}`}
      >
        <div
          className={pct >= 100 ? "h-full bg-destructive" : "h-full bg-primary"}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

/**
 * The pending change in one sentence. `current` is the subscription's own
 * tier, which a lapsed grace period can leave different from the plan's.
 */
function scheduledChangeSentence(current: PlanTier, change: ScheduledChangeResp): string {
  const switchTo =
    change.tier === current
      ? `Switching to ${intervalAdjective(change.interval)} billing`
      : `Switching to ${TIER_LABELS[change.tier]}, billed ${intervalAdverb(change.interval)},`;
  return `${switchTo} on ${formatDate(change.effectiveAt)}. Until then you keep ${TIER_LABELS[current]}.`;
}

/**
 * The action that drops the pending change, named for what it keeps — the
 * same tier-or-interval split `scheduledChangeSentence` uses. A change that
 * keeps the tier only switches interval, so the interval kept is the other
 * one; the subscription's own interval can be unknown.
 */
function keepCurrentLabel(current: PlanTier, change: ScheduledChangeResp): string {
  if (change.tier !== current) {
    return `Keep ${TIER_LABELS[current]}`;
  }
  const kept: BillingInterval = change.interval === "month" ? "year" : "month";
  return `Keep ${intervalAdjective(kept)} billing`;
}

/**
 * Why the plan options offer no changes right now, or null when they can.
 * In each case a change would not do what its dialog promises: a plan set to
 * cancel still ends on schedule, a past-due subscription takes the new price
 * without restoring the plan, and with Stripe unreachable the subscription
 * the change would act on is unknown.
 */
function changeLockReason(plan: PlanResp): string | null {
  const subscription = plan.subscription;
  if (subscription?.cancelAtPeriodEnd) return "Resume your plan to change it.";
  if (subscription?.status === "past_due") return "Update your card to change your plan.";
  if (!subscription && plan.source === "stripe") {
    return "Plan changes are unavailable right now. Try again in a few minutes.";
  }
  return null;
}

const INTERVAL_LABELS: Record<BillingInterval, string> = { month: "Monthly", year: "Annual" };

/** Which price the plan options show — and buy. */
const IntervalToggle = ({
  value,
  onChange,
}: {
  value: BillingInterval;
  onChange: (interval: BillingInterval) => void;
}) => (
  <ButtonGroup aria-label="Billing interval">
    {BILLING_INTERVALS.map((interval) => (
      <Button
        key={interval}
        size="sm"
        variant={interval === value ? "default" : "outline"}
        aria-pressed={interval === value}
        onClick={() => onChange(interval)}
        data-statsig-name={`billing-interval-${interval}`}
      >
        {INTERVAL_LABELS[interval]}
      </Button>
    ))}
  </ButtonGroup>
);

/**
 * Free, then every paid plan at the chosen interval, with the one action that makes
 * sense from where the user stands: buy it, change to it (with the timing the
 * server will apply), or nothing because they are already on it.
 */
const PlanOptions = ({
  plan,
  interval,
  isBusy,
  isLocked,
  onChange,
}: {
  plan: PlanResp;
  interval: BillingInterval;
  isBusy: boolean;
  isLocked: boolean;
  onChange: (intent: PlanChangeIntent) => void;
}) => {
  // One checkout for the whole list: a second Upgrade clicked while the first
  // is still on its way to Stripe would open a second Checkout session.
  const checkout = useStartCheckout();
  return (
    <ul className="divide-y rounded-md border" data-testid="plan-options">
      <FreePlanOption plan={plan} isBusy={isBusy} isLocked={isLocked} onChange={onChange} />
      {PAID_TIERS.map((tier) => (
        <PlanOption
          key={tier}
          plan={plan}
          tier={tier}
          interval={interval}
          isBusy={isBusy}
          isLocked={isLocked}
          checkout={checkout}
          onChange={onChange}
        />
      ))}
    </ul>
  );
};

/**
 * Free, listed with the paid plans so the way down sits next to the ways up.
 * Free is the absence of a subscription, so moving to it is a cancellation at
 * the period end and goes through the same dialog as "Cancel plan". A plan
 * already set to end shows Free as scheduled; one held by a grant, or whose
 * subscription can't be read, has nothing on this row to act on.
 */
const FreePlanOption = ({
  plan,
  isBusy,
  isLocked,
  onChange,
}: {
  plan: PlanResp;
  isBusy: boolean;
  isLocked: boolean;
  onChange: (intent: PlanChangeIntent) => void;
}) => {
  const subscription = plan.subscription;
  let action: ReactNode = null;
  if (plan.tier === "free") {
    action = <Badge variant="secondary">Current plan</Badge>;
  } else if (plan.grant) {
    // A grant keeps its tier whatever happens to the subscription, so ending
    // the subscription would not land on Free. "Cancel plan" stays available.
    action = null;
  } else if (subscription?.cancelAtPeriodEnd) {
    action = <Badge variant="outline">Scheduled</Badge>;
  } else if (subscription && !isLocked) {
    action = (
      <Button
        size="sm"
        variant="outline"
        disabled={isBusy}
        onClick={() => onChange({ kind: "cancel", toFree: true })}
        data-statsig-name="billing-plan-switch-free"
      >
        Switch to Free
      </Button>
    );
  }

  return (
    <li
      className="px-3 py-2.5 flex items-center justify-between gap-3"
      data-testid="plan-option-free"
    >
      <div className="min-w-0">
        <div className="text-sm font-medium">{TIER_LABELS.free}</div>
        <div className="text-xs text-muted-foreground">
          {FREE_PRICE_LABEL} · {TIER_TAGLINES.free}
        </div>
      </div>
      {action}
    </li>
  );
};

/**
 * One plan at the chosen interval. Its action is "Current plan" when changing
 * to it would change nothing, "Included" when a grant already gives that tier,
 * "Scheduled" when that change is already set for the period end, a change
 * button timed by `planChangeTiming`, or — without a subscription — checkout
 * for a tier above the one held. A locked card offers only the "Current plan"
 * badge.
 */
const PlanOption = ({
  plan,
  tier,
  interval,
  isBusy,
  isLocked,
  checkout,
  onChange,
}: {
  plan: PlanResp;
  tier: PaidTier;
  interval: BillingInterval;
  isBusy: boolean;
  isLocked: boolean;
  checkout: ReturnType<typeof useStartCheckout>;
  onChange: (intent: PlanChangeIntent) => void;
}) => {
  const entry = catalogEntry(plan, tier);
  const multiple = multipleLabel(entry);
  const price = entry?.prices[interval] ?? null;
  const subscription = plan.subscription;

  let action: ReactNode = null;
  if (subscription) {
    const timing = planChangeTiming(
      { tier: subscription.tier, interval: subscription.interval ?? "month" },
      { tier, interval },
    );
    const scheduled = subscription.scheduledChange;
    // A grant above the subscription already gives this tier's allowance, so
    // paying for it now would buy nothing the dialog could promise. Matches
    // the no-subscription branch, which only sells tiers above the one held.
    const grantCovers =
      plan.grant !== null &&
      tier !== subscription.tier &&
      timing === "immediate" &&
      !paidTiersAbove(plan.tier).includes(tier);
    if (timing === "noop") {
      action = <Badge variant="secondary">Current plan</Badge>;
    } else if (grantCovers) {
      action = <Badge variant="secondary">Included</Badge>;
    } else if (isLocked) {
      action = null;
    } else if (scheduled?.tier === tier && scheduled.interval === interval) {
      // Asking again would only replace the change with itself.
      action = <Badge variant="outline">Scheduled</Badge>;
    } else if (price) {
      action = (
        <Button
          size="sm"
          variant={timing === "immediate" ? "default" : "outline"}
          disabled={isBusy}
          onClick={() => onChange({ kind: "change", tier, interval, timing })}
          data-statsig-name={`billing-plan-change-${tier}-${interval}`}
        >
          {changeLabel(subscription.tier, tier, interval, timing)}
        </Button>
      );
    }
  } else if (tier === plan.tier) {
    // Held without a live subscription: a hand-made grant, or a Stripe plan
    // whose details could not be read.
    action = <Badge variant="secondary">Current plan</Badge>;
  } else if (!isLocked && paidTiersAbove(plan.tier).includes(tier) && price) {
    action = (
      <Button
        size="sm"
        // Still held once checkout succeeds: the page is navigating to Stripe,
        // and another click would open a second session.
        disabled={checkout.isPending || checkout.isSuccess}
        onClick={() => checkout.mutate({ tier, interval })}
        data-statsig-name={`billing-upgrade-${tier}-${interval}`}
      >
        Upgrade
      </Button>
    );
  }

  return (
    <li
      className="px-3 py-2.5 flex items-center justify-between gap-3"
      data-testid={`plan-option-${tier}`}
    >
      <div className="min-w-0">
        <div className="text-sm font-medium flex items-center gap-2">
          <span>{TIER_LABELS[tier]}</span>
          {multiple && <span className="text-xs text-muted-foreground">{multiple}</span>}
        </div>
        <div className="text-xs text-muted-foreground">
          {unavailableReason(entry, interval) ??
            [
              price ? formatPricePerInterval(price, interval) : null,
              entry?.marketedUnlimited ? "Effectively unlimited" : null,
            ]
              .filter((part) => part !== null)
              .join(" · ")}
        </div>
      </div>
      {action}
    </li>
  );
};

/**
 * Why a plan has no price at this interval, or null when it has one. A missing
 * annual price is either a tier not sold annually or one this environment
 * can't price right now; the catalog doesn't say which, so the label points
 * to the monthly price without claiming annual doesn't exist.
 */
function unavailableReason(
  entry: CatalogEntryResp | undefined,
  interval: BillingInterval,
): string | null {
  if (entry?.prices[interval]) return null;
  if (interval === "year" && entry?.prices.month) return "Available monthly";
  return "Not available right now";
}

/** The button for moving to a plan, worded by what the move does. */
function changeLabel(
  current: PaidTier,
  tier: PaidTier,
  interval: BillingInterval,
  timing: Exclude<PlanChangeTiming, "noop">,
): string {
  if (tier === current) return `Switch to ${intervalAdjective(interval)}`;
  return timing === "immediate" ? "Upgrade" : "Switch";
}

export const PaymentMethodsCard = () => {
  const { data, isLoading } = usePaymentMethods();
  const refresh = useRefreshPaymentMethods();
  const deleteMethod = useDeletePaymentMethod();
  const setDefault = useSetDefaultPaymentMethod();
  const [adding, setAdding] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<PaymentMethodResp | null>(null);
  const [lastCardWarning, setLastCardWarning] = useState<PaymentMethodResp | null>(null);

  if (isLoading) {
    return (
      <div className="py-6 flex justify-center">
        <Loader size="sm" />
      </div>
    );
  }

  const methods = data?.methods ?? [];

  const confirmRemoval = (method: PaymentMethodResp) => {
    setPendingRemoval(null);
    deleteMethod.mutate(
      { id: method.id },
      {
        onError: (err) => {
          // The server refuses the first attempt when this is the last card
          // under an active plan; show what that costs, then let them decide.
          if (isLastMethodBlocked(err)) setLastCardWarning(method);
        },
      },
    );
  };

  return (
    <div className="space-y-3">
      {methods.length > 0 ? (
        <ul className="divide-y rounded-lg bg-white overflow-hidden">
          {methods.map((method) => (
            <li
              key={method.id}
              className="px-4 py-3 flex items-center justify-between gap-3"
              data-testid="payment-method-row"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium flex items-center gap-2">
                  <span>
                    {formatBrand(method.brand)} •••• {method.last4}
                  </span>
                  {method.isDefault && <Badge variant="secondary">Default</Badge>}
                  {isExpired(method) && <Badge variant="destructive">Expired</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">
                  Expires {String(method.expMonth).padStart(2, "0")}/{method.expYear}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!method.isDefault && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDefault.mutate(method.id)}
                    data-statsig-name="billing-set-default"
                  >
                    Make default
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPendingRemoval(method)}
                  data-statsig-name="billing-remove-card"
                >
                  Remove
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-sm text-muted-foreground italic">
          No saved cards. Add one to pay for a plan.
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAdding(true)}
          data-statsig-name="billing-add-card-open"
        >
          <CirclePlus className="size-4" />
          Add payment method
        </Button>
        {/* Unconditional on purpose: an empty list is the state most likely
            to be wrong — a card saved at Checkout whose webhook has not landed
            yet — and hiding the repair button there leaves no way out. */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
          title="Re-read your saved cards from Stripe"
          data-statsig-name="billing-refresh"
        >
          <RefreshCw className="size-4" />
          Refresh
        </Button>
      </div>

      <AddPaymentMethodDialog open={adding} onOpenChange={setAdding} />

      <AlertDialog
        open={pendingRemoval !== null}
        onOpenChange={(open) => !open && setPendingRemoval(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this card?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRemoval &&
                `${formatBrand(pendingRemoval.brand)} •••• ${pendingRemoval.last4} will no longer be saved for future charges.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep card</AlertDialogCancel>
            <AlertDialogAction onClick={() => pendingRemoval && confirmRemoval(pendingRemoval)}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={lastCardWarning !== null}
        onOpenChange={(open) => !open && setLastCardWarning(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>This card pays for your plan</AlertDialogTitle>
            <AlertDialogDescription>
              It's the only card on file. If you remove it, your next renewal will fail. Your plan
              keeps working for 15 days after that while Okthx retries, and then ends. Add another
              card first if you want to avoid that.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep card</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!lastCardWarning) return;
                deleteMethod.mutate({ id: lastCardWarning.id, confirm: true });
                setLastCardWarning(null);
              }}
              data-statsig-name="billing-remove-last-card-confirm"
            >
              Remove anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
